import { useState, useEffect, useRef, useCallback } from 'react';
import { notification } from 'antd';
import { myApi } from '../../../assets/utils/lib';

/**
 * Custom hook for tracking bulk email job progress via HTTP Polling
 * 
 * @param {string} jobId - Email job ID to track
 * @param {boolean} enabled - Whether to enable polling
 * @param {number} pollingInterval - HTTP polling interval in ms (default: 2000ms)
 * @returns {Object} - Progress state and helper functions
 */
export function useBulkUploadProgress(jobId, enabled = true, pollingInterval = 2000) {
  const [progress, setProgress] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const pollingIntervalRef = useRef(null);
  const lastStatusRef = useRef(null);
  const isMountedRef = useRef(true);
  const errorCountRef = useRef(0);
  const maxErrors = 3;

  /**
   * Fetch current job progress status from the server
   */
  const fetchProgress = async () => {
    if (!jobId) return;

    try {
      const response = await myApi('GET', `/v2/corporations/email-jobs/${jobId}/status`);
      const data = response?.data;

      // Only update if component is still mounted
      if (!isMountedRef.current) return;

      if (data && Number(data.error) === 0) {
        // Reset error count on successful fetch
        errorCountRef.current = 0;
        setError(null);

        // Handle different status states
        handleProgressUpdate(data.data);
      } else {
        throw new Error(data?.message || 'Failed to fetch job status');
      }

    } catch (err) {
      if (!isMountedRef.current) return;

      errorCountRef.current++;
      console.error('Error fetching email job progress:', err);

      // Stop polling after too many errors
      if (errorCountRef.current >= maxErrors) {
        setError('Failed to fetch progress after multiple attempts');
        stopPolling();
        
        notification.error({
          message: 'Connection Error',
          description: 'Unable to fetch email job progress. Please refresh the page.',
          placement: 'topRight',
          duration: 5
        });
      }
    }
  };

  /**
   * Handle progress data updates from email job status
   */
  const handleProgressUpdate = (data) => {
    const { status, progress_percentage, total, sent, failed, error_message } = data;
    const previousStatus = lastStatusRef.current;
    const remaining = (total || 0) - (sent || 0) - (failed || 0);

    // Update last status
    lastStatusRef.current = status;

    switch (status) {
      case 'queued':
      case 'started':
        // Only show notification on status change
        if (previousStatus !== 'queued' && previousStatus !== 'started') {
          setIsProcessing(true);
          setProgress({
            percent: 0,
            sent: 0,
            failed: 0,
            total: total || 0,
            remaining: total || 0
          });
          notification.info({
            message: 'Sending Email Notifications',
            description: `Starting to send ${total || 0} welcome emails...`,
            placement: 'topRight',
            duration: 3
          });
        }
        break;

      case 'processing':
        setIsProcessing(true);
        setProgress({
          percent: progress_percentage || 0,
          sent: sent || 0,
          failed: failed || 0,
          total: total || 0,
          remaining: remaining
        });
        break;

      case 'completed':
        const duration = data.duration || Math.round((new Date() - new Date(data.started_at)) / 1000);
        setProgress({
          percent: 100,
          sent: sent || 0,
          failed: failed || 0,
          total: total || 0,
          remaining: 0,
          duration: duration
        });
        setIsProcessing(false);

        // Only show notification on status change
        if (previousStatus !== 'completed') {
          const hasFailures = (failed || 0) > 0;
          notification[hasFailures ? 'warning' : 'success']({
            message: 'Email Notifications Complete',
            description: `✅ ${sent || 0} sent ${hasFailures ? `• ✗ ${failed} failed` : ''} • ⏱️ ${duration}s`,
            placement: 'topRight',
            duration: 5
          });

          // Clear progress after 10 seconds
          setTimeout(() => {
            if (isMountedRef.current) {
              setProgress(null);
              lastStatusRef.current = null;
            }
          }, 10000);
        }

        // Stop polling when completed
        stopPolling();
        break;

      case 'failed':
        setIsProcessing(false);
        setProgress(null);
        
        if (previousStatus !== 'failed') {
          notification.error({
            message: 'Email Notification Error',
            description: error_message || 'Failed to send email notifications',
            placement: 'topRight',
            duration: 5
          });
        }
        
        // Stop polling on failure
        stopPolling();
        break;

      default:
        // Unknown status
        console.log('Unknown email job status:', status);
        break;
    }
  };

  /**
   * Stop HTTP polling
   */
  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      console.log('Stopping HTTP polling');
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  /**
   * Reset progress state
   */
  const resetProgress = () => {
    setProgress(null);
    setIsProcessing(false);
    setError(null);
    lastStatusRef.current = null;
    errorCountRef.current = 0;
  };

  // Setup HTTP polling on mount and cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;

    if (enabled && jobId) {
      stopPolling();
      console.log(`Starting HTTP polling for email job ${jobId} every ${pollingInterval}ms`);
      fetchProgress();
      pollingIntervalRef.current = setInterval(() => fetchProgress(), pollingInterval);
    }

    // Cleanup function
    return () => {
      isMountedRef.current = false;
      stopPolling();
    };
  }, [jobId, enabled, pollingInterval]); // eslint-disable-line react-hooks/exhaustive-deps -- fetchProgress inlined

  return {
    progress,
    isProcessing,
    resetProgress,
    error,
    isConnected: pollingIntervalRef.current !== null // true when polling is active
  };
}

export default useBulkUploadProgress;

